import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './contexts/ToastContext';
import { QuotationProvider } from './contexts/QuotationContext';
import { initAnalytics } from './utils/analytics';

// Public Pages
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage'; // Added ResetPasswordPage import
import DashboardPage from './pages/public/DashboardPage';
import ProductsPage from './pages/public/ProductsPage';
import ProductDetailPage from './pages/public/ProductDetailPage';
// REMOVE one of the detail routes to avoid conflict!
// import SingleProductPage from './pages/public/SingleProductPage';
import CategoriesPage from './pages/public/CategoriesPage';
import CartPage from './pages/public/CartPage';
import CheckoutPage from './pages/public/CheckoutPage';
import OrderConfirmationPage from './pages/public/OrderConfirmationPage';
import QuotationPage from './pages/public/QuotationPage';
import QuotationConfirmationPage from './pages/public/QuotationConfirmationPage';
import QuotationPublicPage from './pages/public/QuotationPublicPage';
import MyQuotationsPage from './pages/public/MyQuotationsPage';
import ManageOrdersPage from './pages/public/ManageOrdersPage';
import ContactDetailsPage from './pages/public/ContactDetailsPage';
import AccountPage from './pages/public/AccountPage';
import UserProfilePage from './pages/public/UserProfilePage';
import EventRegistrationPage from './pages/public/EventRegistrationPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCategories from './pages/admin/AdminCategories';
import AdminProducts from './pages/admin/AdminProducts';
import AdminStories from './pages/admin/AdminStories';
import AdminSettings from './pages/admin/AdminSettings';
import AdminProfile from './pages/admin/AdminProfile';
import AdminUsers from './pages/admin/AdminUsers';
import AdminECatalogue from './pages/admin/AdminE-Catalogue';
import AdminSlider from './pages/admin/AdminSlider';
import AdminHeroSlider from './pages/admin/AdminHeroSlider';
import AdminEvents from './pages/admin/AdminEvents';
import AdminEventRegistrations from './pages/admin/AdminEventRegistrations';
import AdminOrders from './pages/admin/AdminOrders';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminQuotations from './pages/admin/AdminQuotations';
import FreezeHistoryPage from './pages/admin/FreezeHistoryPage';

// Layouts
import AdminLayout from './components/layouts/AdminLayout';

// Axios default base URL & interceptor
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected Route component (redirects to /admin/login if no token)
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate a brief spinner for initial load
    const timer = setTimeout(() => setIsLoading(false), 800);

    // Initialize analytics tracking
    console.log('🔍 App: Initializing analytics...');
    initAnalytics();
    console.log('🔍 App: Analytics initialization called');

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <CartProvider>
      <ToastProvider>
        <QuotationProvider>
          <Router>
            <Routes>
              {/* ─── Public Pages: NO layout ─────────────────────────────── */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:id/:token" element={<ResetPasswordPage />} /> {/* Added Route */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmationPage />} />
              <Route path="/quotation" element={<QuotationPage />} />
              <Route path="/quotation-confirmation/:quotationNumber" element={<QuotationConfirmationPage />} />
              <Route path="/q/:quotationNumber" element={<QuotationPublicPage />} />
              <Route path="/my-quotations" element={<MyQuotationsPage />} />
              <Route path="/manage-orders" element={<ManageOrdersPage />} />
              <Route path="/contact-details" element={<ContactDetailsPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/event-register/:id" element={<EventRegistrationPage />} />

              {/* ─── Admin Login ─────────────────────────────────────────── */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* ─── Admin Protected Routes ─────────────────────────────── */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="story-updates" element={<AdminStories />} />
                <Route path="e-catalogue" element={<AdminECatalogue />} />
                <Route path="slider" element={<AdminSlider />} />
                <Route path="hero-slider" element={<AdminHeroSlider />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="event-registrations" element={<AdminEventRegistrations />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="quotations" element={<AdminQuotations />} />
                <Route path="freeze-history" element={<FreezeHistoryPage />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="users" element={<AdminUsers />} />
              </Route>

              {/* ─── Fallback: redirect unknown paths to home ───────────── */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </QuotationProvider>
      </ToastProvider>
    </CartProvider>
  );
}

export default App;
